/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import { useActions } from 'common/hooks/useActions';
import RowAction from 'components/rowAction';
import EventFilterPopup from '../filterForm';
import PaginationApp from 'components/pagination';

import * as S from './styles';
import * as T from 'common/types';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <circle cx="17" cy="8" r="2.5" />
    <path d="M16 14.5a5 5 0 0 1 5 5.5" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

interface IProps {
  data: { list: []; totalPages?: number };
  departments: [];
  loading: boolean;
}

export default function EventsTable({ data, departments, loading }: IProps) {
  const { eventsRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: ''
  });

  const fetchData = () => {
    eventsRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangePageSize = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e, page: 1 })}
            placeholder="Search events"
            value={meta.search}
          />
          <ToolbarRight>
            <EventFilterPopup setFilter={setMeta} filter={meta} departments={departments} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 72 },
            { id: 4, key: 'title', title: 'Title', dir: 'left' },
            /*
             * 120, not 85. `EventTypeEnum` is a closed set and its longest
             * value, `Entertainment`, measures 85px — so at a width of 85 the
             * only column on this screen whose full vocabulary is knowable in
             * advance was the one being cut. Found by the width sweep in
             * group 4; the 35px comes out of Title, which clips either way.
             */
            { id: 5, key: 'type', title: 'Type', dir: 'left', width: 120 },
            { id: 6, key: 'capacity', title: 'Capacity', type: 'num', width: 90 },
            { id: 7, key: 'department', title: 'Department', dir: 'left', width: 105 },
            { id: 7, key: 'duration', title: 'Duration', dir: 'left', width: 92 },
            { id: 8, key: 'startsAt', title: 'Start Date', dir: 'left', width: 156 },
            /* Edit and Contributors in one column, as everywhere else. Edit
               was a headed column of its own and Contributors was a bordered
               button down every row — 194px between them for two icons. */
            { id: 9, key: 'edit', title: '', type: 'actions', width: 96 }
          ]}
          data={data.list}
          empty={
            meta.search ? (
              <Empty
                title={`No events match “${meta.search}”`}
                text="Check the spelling, or clear the search to see every event."
              />
            ) : (
              <Empty
                icon={<CalendarIcon />}
                title="No events yet"
                text="Create an event and it appears here with its capacity and contributor list."
              />
            )
          }
          loading={loading}
          altanfeethiFields={{
            id: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),

            /* The 50-character JS cut is gone; AppTable clips and titles. */
            title: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.title}</Value>
              </S.Div>
            ),
            /* Still plain text — see the Step 5 decision in FINDINGS. Every
               value of `EventTypeEnum` is already the word a person writes. */
            type: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.type}</Value>
              </S.Div>
            ),
            capacity: (item: T.Pages.EventItem) => <S.Div>{item?.capacity ?? 0}</S.Div>,
            department: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.department}</Value>
              </S.Div>
            ),
            duration: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.duration}</Value>
              </S.Div>
            ),
            startsAt: (item: T.Pages.EventItem) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.startsAt)}</Value>
              </S.Div>
            ),
            edit: (item: T.Pages.EventItem) => (
              <>
                <RowAction to={`/panel/events/${item.id}`} label="Edit event">
                  <PenIcon />
                </RowAction>
                {/*
                 * A public event has no contributor list to open — anyone may
                 * attend — so the action is disabled and says why, rather
                 * than the button relabelling itself "Public", which read as
                 * a status in an actions column.
                 */}
                <RowAction
                  to={`/panel/events/contributors/${item.id}`}
                  disabled={item?.isPublic}
                  label={item?.isPublic ? 'Public event — open to everyone' : 'See who is attending'}
                >
                  <PeopleIcon />
                </RowAction>
              </>
            )
          }}
        />
      </PanelBoxLayout>
      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
